/* eslint-disable @typescript-eslint/camelcase */
import { CredRowErrors, CredRowStatuses, Credential, Paginated, ActionsType } from '@card/types';
import {
  GetCredentialsOptions,
  ValidateCredentialParams,
  GetBlockedCountResponse
} from '@card/commonlib';
import { createAction, createErrorAction } from '../../actions/actionCreator';
import Constants from '../../constants';

export enum CredentialsActionType {
  SaveEditedCredential = 'credentials/save_edited',
  Validate = 'credentials/validate',
  ValidateSuccess = 'credentials/validate_success',
  ValidateError = 'credentials/validate_error',
  SetStatus = 'credentials/set_status',
  SetError = 'credentials/set_error',
  Fetch = 'credentials/fetch',
  FetchSuccess = 'credentials/fetch_success',
  FetchFailed = 'credentials/fetch_failed',
  DeleteCredential = 'credentials/delete',
  DeleteCredentialSuccess = 'credentials/delete_success',
  DeleteCredentialError = 'credentials/delete_error',
  EnableAutoRenewal = 'credentials/enable_auto_renewal',
  DisableAutoRenewal = 'credentials/disable_auto_renewal',
  ToggleAutomaticRenewal = 'credentials/toggle_automatic_renewal',
  ToggleAutomaticRenewalSuccess = 'credentials/toggle_automatic_renewal_success',
  ToggleAutomaticRenewalFailed = 'credentials/toggle_automatic_renewal_failed',
  ToggleAutoRenewAllCredentials = 'credentials/toggle_auto_renew_all',
  ToggleAutoRenewAllCredentialsSuccess = 'credentials/toggle_auto_renew_all_success',
  ToggleAutoRenewAllCredentialsFailed = 'credentials/toggle_auto_renew_all_failed',
  GetPassword = 'credentials/get_password',
  GetPasswordSuccess = 'credentials/get_password_success',
  GetMercadopagoLink = 'credential/get_mercadopago_link',
  GetMercadopagoLinkSuccess = 'credential/get_mercadopago_link_successs',
  FetchBlocked = 'credentials/fetch_blocked',
  FetchBlockedSuccess = 'credentials/fetch_blocked_success',
  FetchBlockedFailed = 'credentials/fetch_blocked_failed',
  FetchBlockedCountStarted = 'credentials/fetch_blocked_count_started',
  FetchBlockedCountSuccess = 'credentials/fetch_blocked_count_success',
  FetchBlockedCountFailed = 'credentials/fetch_blocked_count_failed'
}

export const CredentialsActions = {
  validate: (credential: ValidateCredentialParams) =>
    createAction(CredentialsActionType.Validate, credential),
  saveEditedCredential: ({ password, idCredential }: { password: string; idCredential: string }) =>
    createAction(CredentialsActionType.SaveEditedCredential, { password, idCredential }),
  validateSuccess: () => createAction(CredentialsActionType.ValidateSuccess),
  validateFailed: (errorMessage: keyof CredRowErrors) =>
    createErrorAction(CredentialsActionType.ValidateError, errorMessage),
  setStatus: (status: keyof CredRowStatuses) =>
    createAction(CredentialsActionType.SetStatus, status),
  setError: (status: keyof CredRowErrors) => createAction(CredentialsActionType.SetError, status),
  fetch: ({ per_page, ...rest }: GetCredentialsOptions = {}) =>
    createAction(CredentialsActionType.Fetch, {
      per_page: per_page || Constants.MEDIUM_PAGE_SIZE,
      ...rest
    }),
  fetchSuccess: (data: Paginated<Credential>) =>
    createAction(CredentialsActionType.FetchSuccess, data),
  fetchFailed: (error: Error) => createErrorAction(CredentialsActionType.FetchFailed, error),
  deleteCredential: (credentialId: string) =>
    createAction(CredentialsActionType.DeleteCredential, credentialId),
  deleteCredentialSuccess: () => createAction(CredentialsActionType.DeleteCredentialSuccess),
  deleteCredentialFailed: (error: Error) =>
    createErrorAction(CredentialsActionType.DeleteCredentialError, error),
  enableAutoRenewal: (credentialId: string) =>
    createAction(CredentialsActionType.EnableAutoRenewal, credentialId),
  disableAutoRenewal: (credentialId: string) =>
    createAction(CredentialsActionType.DisableAutoRenewal, credentialId),
  toggleAutomaticRenewal: (credentialId: string, enableAutomaticRenewal: boolean) =>
    createAction(CredentialsActionType.ToggleAutomaticRenewal, {
      credentialId,
      enableAutomaticRenewal
    }),
  toggleAutomaticRenewalSuccess: () =>
    createAction(CredentialsActionType.ToggleAutomaticRenewalSuccess),
  toggleAutomaticRenewalFailed: (error: Error) =>
    createErrorAction(CredentialsActionType.ToggleAutomaticRenewalFailed, error),
  autoRenewAllCredentials: (value: boolean) =>
    createAction(CredentialsActionType.ToggleAutoRenewAllCredentials, value),
  autoRenewAllCredentialsSuccess: () =>
    createAction(CredentialsActionType.ToggleAutoRenewAllCredentialsSuccess),
  autoRenewAllCredentialsFailed: (error: Error) =>
    createErrorAction(CredentialsActionType.ToggleAutoRenewAllCredentialsFailed, error),
  getPassword: (credentialId: string) =>
    createAction(CredentialsActionType.GetPassword, credentialId),
  getPasswordSuccess: (credentialId: string, password: string) =>
    createAction(CredentialsActionType.GetPasswordSuccess, {
      credentialId,
      password
    }),
  getMercadopagoLink: () => createAction(CredentialsActionType.GetMercadopagoLink),
  getMercadopagoLinkSuccess: (mercadopagoActivationUrl: string) =>
    createAction(CredentialsActionType.GetMercadopagoLinkSuccess, {
      mercadopagoActivationUrl
    }),
  fetchBlocked: ({ page }: { page?: number } = {}) =>
    createAction(CredentialsActionType.FetchBlocked, {
      page: page,
      per_page: Constants.MEDIUM_PAGE_SIZE
    }),
  fetchBlockedSuccess: (data: Paginated<Credential>) =>
    createAction(CredentialsActionType.FetchBlockedSuccess, data),
  fetchBlockedFailed: (error: Error) =>
    createErrorAction(CredentialsActionType.FetchBlockedFailed, error),
  fetchBlockedCountStarted: () => createAction(CredentialsActionType.FetchBlockedCountStarted),
  fetchBlockedCountSuccess: (data: GetBlockedCountResponse) =>
    createAction(CredentialsActionType.FetchBlockedCountSuccess, data),
  fetchBlockedCountFailed: (error: Error) =>
    createAction(CredentialsActionType.FetchBlockedCountFailed, error)
};

export type CredentialsAction = ActionsType<typeof CredentialsActions>;
